import { AlertCircle, Check, Eye, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import { DragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { formatFileSize } from '@/components/documents-v2/utils/fileUtils';
import { FullDocumentViewer } from '@/components/initial-review/components/FullDocumentViewer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useDestroyBlobMutation, useGetBlobsQuery } from '@/services/api/blobService';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';
import { useFilevineIngestionStatusQuery } from '@/services/api/generated/filevineSyncService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';
import type { IngestionSections } from '@/services/types/case-ingestion-types';
import { getCmsTargetSystemFromUrl, hasCmsAutoSync } from '@/utils/externalSystemUtils';

import { useDocumentTaskCompletion } from '../../hooks';
import { IngestionStepLayout } from '../../layout';
import { IngestionStepSkeleton } from '../../skeletons';
import { DocumentReceivedCard } from './DocumentReceivedCard';

interface DocumentsStepProps {
  matterId: string;
  sectionStatuses: IngestionSections;
  onSaveComplete?: (success: boolean) => void;
}

/**
 * DocumentsStep - Document upload step with intake document tracking.
 *
 * Allows:
 * 1. Marking Retainer Agreement and HIPAA Directive as received/not received
 * 2. Uploading additional relevant documents
 *
 * The received/not received tracking updates both task status and section status.
 */
export const DocumentsStep = ({
  matterId,
  sectionStatuses,
  onSaveComplete,
}: DocumentsStepProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showManualUpload, setShowManualUpload] = useState(false);
  const [previewArtifactId, setPreviewArtifactId] = useState<string | null>(null);
  const hasHydrated = useRef(false);

  // Read CMS URL from matter API (source of truth after CaseInfo PATCH)
  const { data: matter } = useGetMatterDetailsQuery(matterId);
  const detectedCms = useMemo(
    () => getCmsTargetSystemFromUrl(matter?.external_cms_url),
    [matter?.external_cms_url],
  );
  const hasAutoSync = useMemo(() => hasCmsAutoSync(detectedCms), [detectedCms]);

  // Document task completion hook for Retainer/HIPAA tracking
  const {
    retainerStatus,
    hipaaStatus,
    handleReceived,
    handleNotReceived,
    isLoading: isTaskLoading,
  } = useDocumentTaskCompletion(matterId, sectionStatuses);

  // Fetch existing documents
  const { data: existingBlobs, isLoading: isLoadingBlobs, refetch: refetchBlobs } = useGetBlobsQuery(
    matterId,
  );

  // Get root collection for uploads (same pattern as DocumentExplorer)
  const { data: collectionTree, isLoading: isLoadingCollections } = useGetCollectionTreeQuery(
    matterId,
  );
  const rootCollectionId = collectionTree?.id;

  // Production upload manager (same pattern as DocumentExplorer)
  const { uploadProgress, handleUploadToCollection } = useUploadManagerUnified({
    matterId,
    refetch: refetchBlobs,
  });

  // Delete mutation
  const [destroyBlob, { isLoading: isDeleting }] = useDestroyBlobMutation();

  // Filevine sync status - poll while in progress
  const { data: ingestionStatus } = useFilevineIngestionStatusQuery(
    { matterId },
    {
      pollingInterval: 5000, // Poll every 5 seconds
    },
  );
  const isFilevineSyncRunning = ingestionStatus?.status === 'IN_PROGRESS';
  const isFilevineSyncFailed = ingestionStatus?.status === 'FAILED';

  // Refetch blobs when Filevine sync completes
  useEffect(() => {
    if (ingestionStatus?.status === 'COMPLETED') {
      refetchBlobs();
    }
  }, [ingestionStatus?.status, refetchBlobs]);

  const handleDeleteFile = async (blobId: string) => {
    try {
      await destroyBlob({ matterId, blobId }).unwrap();
      refetchBlobs();
    } catch {
      toast.error('Failed to delete file');
    }
  };

  // Derive isUploading from uploadProgress
  const isUploading = uploadProgress.some(
    (p) => p.status === 'uploading' || p.status === 'pending',
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  // Real upload using production-tested hook
  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!rootCollectionId || files.length === 0) return;
      await handleUploadToCollection(files, rootCollectionId, false, false);
    },
    [rootCollectionId, handleUploadToCollection],
  );

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      await uploadFiles(files);
    },
    [uploadFiles],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const files = Array.from(e.target.files);
        uploadFiles(files);
      }
    },
    [uploadFiles],
  );

  const handleComplete = (e?: React.FormEvent) => {
    e?.preventDefault();
    // Documents step is not audit-tracked - just notify completion
    toast.success('Documents step completed');
    onSaveComplete?.(true);
  };

  // Show skeleton during initial load (wait for both queries)
  const isLoadingData = isLoadingBlobs || isLoadingCollections;
  if (isLoadingData && !hasHydrated.current) {
    return <IngestionStepSkeleton />;
  }

  // Mark as hydrated after initial load
  if (!isLoadingData && !hasHydrated.current) {
    hasHydrated.current = true;
  }

  // Filter in-progress uploads (exclude completed that are already in existingBlobs)
  const inProgressUploads = uploadProgress.filter(
    (p) =>
      (p.status === 'uploading' || p.status === 'pending') &&
      !existingBlobs?.some((b) => b.original_filename === p.fileName),
  );

  const totalFileCount =
    (existingBlobs?.length ?? 0) + inProgressUploads.length;

  // Full-screen document preview (same pattern as InitialReviewModal)
  if (previewArtifactId) {
    return (
      <div className="flex flex-col h-[calc(100vh-180px)]">
        <FullDocumentViewer
          artifactId={previewArtifactId}
          matterId={matterId}
          onBack={() => setPreviewArtifactId(null)}
        />
      </div>
    );
  }

  return (
    <IngestionStepLayout
      title="Documents"
      description="Mark intake documents as received and upload any additional files"
    >
      <form id="ingestion-step-form" onSubmit={handleComplete}>
        {/* Intake Document Tracking */}
        <div className="mb-3 space-y-2">
          <DocumentReceivedCard
            title="Signed Retainer Agreement"
            badge="R"
            status={retainerStatus}
            onReceivedClick={() => handleReceived('retainer')}
            onNotReceivedClick={() => handleNotReceived('retainer')}
            isLoading={isTaskLoading || isFilevineSyncRunning}
          />
          <DocumentReceivedCard
            title="Signed HIPAA Directive"
            badge="H"
            status={hipaaStatus}
            onReceivedClick={() => handleReceived('hipaa')}
            onNotReceivedClick={() => handleNotReceived('hipaa')}
            isLoading={isTaskLoading || isFilevineSyncRunning}
          />
        </div>

        {/* Filevine Sync In Progress Banner */}
        {isFilevineSyncRunning && (
          <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              <p className="text-xs font-medium text-blue-700">
                Importing documents from Filevine...
              </p>
            </div>
            <div className="mt-1.5 ml-6 space-y-0.5">
              {ingestionStatus?.started_at && (
                <p className="text-xs text-blue-600">
                  Started {new Date(ingestionStatus.started_at).toLocaleTimeString()}
                </p>
              )}
              {ingestionStatus?.current_phase && (
                <p className="text-xs text-blue-600">
                  Phase: {ingestionStatus.current_phase}
                </p>
              )}
              {(ingestionStatus?.stats?.total_documents_synced ?? 0) > 0 && (
                <p className="text-xs text-blue-600">
                  {ingestionStatus?.stats?.total_documents_synced} documents processed
                </p>
              )}
              <p className="text-xs text-blue-500 italic">
                This may take 1-2 minutes. Documents will appear automatically.
              </p>
            </div>
          </div>
        )}

        {/* Filevine Sync Failed Banner */}
        {isFilevineSyncFailed && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <p className="text-xs font-medium text-red-700">
                Unable to import documents from Filevine
              </p>
            </div>
            <div className="mt-1.5 ml-6 space-y-0.5">
              {(ingestionStatus?.stats?.total_documents_synced ?? 0) > 0 && (
                <p className="text-xs text-red-600">
                  {ingestionStatus?.stats?.total_documents_synced} documents were imported before the error.
                </p>
              )}
              <p className="text-xs text-red-500">
                You can upload documents manually below.
              </p>
            </div>
          </div>
        )}

        {/* Document Upload Area */}
        {hasAutoSync && !showManualUpload && !isFilevineSyncRunning && !isFilevineSyncFailed ? (
          <Card>
            <CardContent className="p-2">
              <div className="flex items-start gap-2">
                <Check className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-zinc-900 dark:text-zinc-100">
                    Automated Document Syncing
                  </p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                    Filevine documents will be pulled automatically when
                    ingestion completes. No manual upload needed.
                  </p>
                  <button
                    type="button"
                    className="text-xs text-zinc-500 underline mt-2 transition-colors hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
                    onClick={() => setShowManualUpload(true)}
                  >
                    Upload manually instead
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className={isFilevineSyncRunning ? 'opacity-60 pointer-events-none' : ''}>
            <CardContent className="p-2">
              {/* Drop zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-zinc-300 hover:border-zinc-400'
                } ${isFilevineSyncRunning ? 'cursor-not-allowed' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload className="h-8 w-8 mx-auto text-zinc-400 mb-1.5" />
                <p className="text-xs font-medium text-zinc-900 mb-0.5">
                  Drag and drop files here
                </p>
                <p className="text-xs text-zinc-500 mb-2">or</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={isUploading || isFilevineSyncRunning}
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  Browse Files
                </Button>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isUploading || isFilevineSyncRunning}
                />
                <p className="text-xs text-zinc-500 mt-2">
                  PDF, Word, Excel, Images and other common formats supported
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unified file list - always visible regardless of sync mode */}
        {totalFileCount > 0 && (
          <Card className="mt-2">
            <CardContent className="p-2">
              <h3 className="text-xs font-semibold text-zinc-900 dark:text-zinc-100 mb-1.5">
                Files ({totalFileCount})
              </h3>
              <div className="space-y-1.5">
                {/* In-progress uploads */}
                {inProgressUploads.map((item) => (
                  <div
                    key={item.fileName}
                    className="flex items-center gap-2 p-1.5 bg-zinc-50 rounded-lg border border-zinc-200 dark:bg-zinc-800/50 dark:border-zinc-700"
                  >
                    <Loader2 className="h-3.5 w-3.5 text-blue-500 flex-shrink-0 animate-spin" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-zinc-900 dark:text-zinc-100 truncate">
                        {item.fileName}
                      </p>
                      <div className="mt-0.5 h-1 bg-zinc-200 dark:bg-zinc-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all duration-200"
                          style={{ width: `${item.progress}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400 flex-shrink-0">
                      {Math.round(item.progress)}%
                    </span>
                  </div>
                ))}

                {/* Existing blobs */}
                {existingBlobs?.map((blob) => (
                  <div
                    key={blob.id}
                    className="flex items-center gap-2 p-1.5 bg-zinc-50 rounded-lg border border-zinc-200 dark:bg-zinc-800/50 dark:border-zinc-700"
                  >
                    <FileText className="h-3.5 w-3.5 text-zinc-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-zinc-900 dark:text-zinc-100 truncate">
                        {blob.original_filename || blob.name}
                      </p>
                    </div>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400 flex-shrink-0">
                      {formatFileSize(blob.file_size)}
                    </span>
                    {blob.artifact_id && (
                      <button
                        type="button"
                        onClick={() => setPreviewArtifactId(blob.artifact_id!)}
                        className="p-1 text-zinc-400 transition-colors hover:text-blue-600 dark:text-zinc-500 dark:hover:text-blue-400"
                        title="Preview document"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteFile(blob.id)}
                      disabled={isDeleting || isFilevineSyncRunning}
                      className="p-1 text-zinc-400 transition-colors hover:text-red-500 disabled:opacity-50 dark:text-zinc-500 dark:hover:text-red-400"
                      title="Delete document"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </form>
    </IngestionStepLayout>
  );
};
